/**
 * GroundRiskMitigations Component
 *
 * Displays the four ground risk mitigations for SORA evidence review.
 * Uses the MitigationCard component to display each mitigation with
 * level, commentary, evidence, and citations.
 *
 * Ground Risk Mitigations:
 * - M1A: Sheltering (Low, Medium, Not applicable)
 * - M1B: Operational Restrictions (Medium, High, Not applicable)
 * - M1C: Ground Observations (Low, Not applicable)
 * - M2: Reducing impact dynamics (Medium, High, Not applicable)
 *
 * Requirements: 5.1, 5.2
 */

import React, { useMemo } from 'react';
import {
  MitigationCard,
  fromGroundRiskMitigation,
} from '../MitigationCard';
import {
  GroundRiskMitigation,
  GroundRiskMitigationCode,
  Citation,
} from '../../types/extraction';
import { Feedback } from '../../types/feedback';
import styles from './GroundRiskMitigations.module.css';

/**
 * Human-readable names for ground risk mitigation codes
 */
const MITIGATION_NAMES: Record<GroundRiskMitigationCode, string> = {
  M1A: 'Sheltering',
  M1B: 'Operational Restrictions',
  M1C: 'Ground Observations',
  M2: 'Reducing impact dynamics',
};

/**
 * Descriptions for each mitigation type
 */
const MITIGATION_DESCRIPTIONS: Record<GroundRiskMitigationCode, string> = {
  M1A: 'Mitigation through sheltering of people on the ground (Low, Medium, Not applicable)',
  M1B: 'Mitigation through operational restrictions limiting exposure (Medium, High, Not applicable)',
  M1C: 'Mitigation through ground observers monitoring the area (Low, Not applicable)',
  M2: 'Mitigation through reducing the impact dynamics of the UAS (Medium, High, Not applicable)',
};

/**
 * Order of mitigations for display
 */
const MITIGATION_ORDER: GroundRiskMitigationCode[] = ['M1A', 'M1B', 'M1C', 'M2'];

/**
 * Props for the GroundRiskMitigations component
 */
export interface GroundRiskMitigationsProps {
  /** Array of ground risk mitigations to display */
  mitigations: GroundRiskMitigation[];
  /** Callback when a citation is clicked */
  onCitationClick: (citation: Citation) => void;
  /** Callback when the feedback button is clicked for a mitigation */
  onFeedbackClick: (mitigationId: string) => void;
  /** Optional map of feedback records by mitigation ID */
  feedbackMap?: Map<string, Feedback>;
  /** Optional loading state */
  isLoading?: boolean;
  /** Optional title for the section */
  title?: string;
  /** Optional description for the section */
  description?: string;
}

/**
 * Sorts mitigations according to the defined order (M1A, M1B, M1C, M2)
 */
function sortMitigationsByOrder(
  mitigations: GroundRiskMitigation[]
): GroundRiskMitigation[] {
  return [...mitigations].sort((a, b) => {
    const indexA = MITIGATION_ORDER.indexOf(a.code);
    const indexB = MITIGATION_ORDER.indexOf(b.code);
    return indexA - indexB;
  });
}

/**
 * GroundRiskMitigations displays the four ground risk mitigations in a grid layout.
 * It uses the MitigationCard component to provide consistent styling and functionality
 * including level badges, LLM commentary, evidence, clickable citations, and feedback buttons.
 *
 * @example
 * <GroundRiskMitigations
 *   mitigations={extractionResults.groundRiskMitigations}
 *   onCitationClick={handleCitationClick}
 *   onFeedbackClick={handleFeedbackClick}
 * />
 */
export const GroundRiskMitigations: React.FC<GroundRiskMitigationsProps> = ({
  mitigations,
  onCitationClick,
  onFeedbackClick,
  feedbackMap,
  isLoading = false,
  title = 'Ground Risk Mitigations',
  description = 'Review the extracted ground risk mitigations for your SORA application. Each mitigation includes LLM-generated commentary and supporting evidence.',
}) => {
  // Sort mitigations by defined order and prepare for rendering
  const sortedMitigations = useMemo(() => {
    return sortMitigationsByOrder(mitigations);
  }, [mitigations]);

  // Count how many of the expected mitigations are present
  const presentMitigationCodes = useMemo(() => {
    return new Set(mitigations.map((m) => m.code));
  }, [mitigations]);

  // Categorise mitigations by selected level for the summary pills
  const statusCounts = useMemo(() => {
    let na = 0;
    let low = 0;
    let medium = 0;
    let high = 0;

    for (const m of mitigations) {
      switch (m.selectedLevel) {
        case 'not_applicable': na++; break;
        case 'low': low++; break;
        case 'medium': medium++; break;
        case 'high': high++; break;
      }
    }

    return { total: mitigations.length, na, low, medium, high };
  }, [mitigations]);

  const mitigationCount = presentMitigationCodes.size;
  const totalExpected = MITIGATION_ORDER.length;

  if (isLoading) {
    return (
      <section
        className={styles.container}
        aria-labelledby="ground-risk-mitigations-title"
        aria-busy="true"
      >
        <header className={styles.header}>
          <h2 id="ground-risk-mitigations-title" className={styles.title}>
            {title}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </header>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} aria-label="Loading mitigations" />
          <p className={styles.loadingText}>Loading ground risk mitigations...</p>
        </div>
      </section>
    );
  }

  if (mitigations.length === 0) {
    return (
      <section
        className={styles.container}
        aria-labelledby="ground-risk-mitigations-title"
      >
        <header className={styles.header}>
          <h2 id="ground-risk-mitigations-title" className={styles.title}>
            {title}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </header>
        <div className={styles.emptyState}>
          <p className={styles.emptyMessage}>
            No ground risk mitigations available. Please process your documents to
            extract evidence.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={styles.container}
      aria-labelledby="ground-risk-mitigations-title"
    >
      <header className={styles.header}>
        <h2 id="ground-risk-mitigations-title" className={styles.title}>
          {title}
        </h2>
        {description && <p className={styles.description}>{description}</p>}
      </header>

      {/* Mitigation Summary Pills */}
      <div className={styles.summaryBar} role="status" aria-label="Mitigation summary">
        <span className={styles.summaryPill}>
          <span className={styles.summaryCount}>{statusCounts.total}</span> mitigations
        </span>
        <span className={styles.summaryPill}>
          <span className={styles.summaryDot} style={{ background: '#a0a0a0' }} aria-hidden="true" />
          <span className={styles.summaryCount}>{statusCounts.na}</span> N/A
        </span>
        <span className={styles.summaryPill}>
          <span className={styles.summaryDot} style={{ background: '#ff8c00' }} aria-hidden="true" />
          <span className={styles.summaryCount}>{statusCounts.low}</span> Low
        </span>
        <span className={styles.summaryPill}>
          <span className={styles.summaryDot} style={{ background: '#0078d4' }} aria-hidden="true" />
          <span className={styles.summaryCount}>{statusCounts.medium}</span> Medium
        </span>
        <span className={styles.summaryPill}>
          <span className={styles.summaryDot} style={{ background: '#107c10' }} aria-hidden="true" />
          <span className={styles.summaryCount}>{statusCounts.high}</span> High
        </span>
      </div>

      {/* Mitigations Grid */}
      <div className={styles.mitigationsGrid}>
        {sortedMitigations.map((mitigation) => {
          const feedback = feedbackMap?.get(mitigation.id);
          const mitigationData = fromGroundRiskMitigation(mitigation, feedback);

          return (
            <MitigationCard
              key={mitigation.id}
              mitigation={mitigationData}
              onCitationClick={onCitationClick}
              onFeedbackClick={onFeedbackClick}
            />
          );
        })}
      </div>

      {/* Footer with count */}
      <footer className={styles.footer}>
        <p className={styles.footerText}>
          <span className={styles.mitigationCount}>{mitigationCount}</span> of{' '}
          <span className={styles.totalMitigations}>{totalExpected}</span> ground
          risk mitigations extracted
        </p>
        {mitigationCount < totalExpected && (
          <p className={styles.missingWarning}>
            Missing mitigations:{' '}
            {MITIGATION_ORDER.filter((code) => !presentMitigationCodes.has(code)).join(
              ', '
            )}
          </p>
        )}
      </footer>
    </section>
  );
};

export default GroundRiskMitigations;
